import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { adminGetSettings, adminUpdateSettings } from "@/lib/api";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminGetSettings().then(setS); }, []);
  if (!s) return <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateSettings(s);
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <h3 className="font-heading text-lg font-bold text-slate-900">Tax</h3>
        <label className="mt-3 flex items-center gap-2 text-sm"><Checkbox checked={s.tax?.enabled} onCheckedChange={(v) => setS({ ...s, tax: { ...s.tax, enabled: !!v } })} data-testid="set-tax-enabled" /> Enable tax</label>
        <div className="mt-3"><Label>Tax rate (%)</Label><Input type="number" value={s.tax?.rate ?? 0} onChange={(e) => setS({ ...s, tax: { ...s.tax, rate: Number(e.target.value) } })} className="mt-1 max-w-[120px]" /></div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <h3 className="font-heading text-lg font-bold text-slate-900">Card Processing Fee</h3>
        <p className="text-xs text-slate-500">Disabled by default. Enable only after confirming disclosure/compliance requirements.</p>
        <label className="mt-3 flex items-center gap-2 text-sm"><Checkbox checked={s.cardFee?.enabled} onCheckedChange={(v) => setS({ ...s, cardFee: { ...s.cardFee, enabled: !!v } })} data-testid="set-cardfee-enabled" /> Enable card fee</label>
        <div className="mt-3"><Label>Fee (%)</Label><Input type="number" value={s.cardFee?.percent ?? 0} onChange={(e) => setS({ ...s, cardFee: { ...s.cardFee, percent: Number(e.target.value) } })} className="mt-1 max-w-[120px]" /></div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <h3 className="font-heading text-lg font-bold text-slate-900">Payments</h3>
        <label className="mt-3 flex items-center gap-2 text-sm"><Checkbox checked={s.payments?.online_enabled} onCheckedChange={(v) => setS({ ...s, payments: { ...s.payments, online_enabled: !!v } })} data-testid="set-online-enabled" /> Online card payment (Stripe)</label>
        <label className="mt-3 flex items-center gap-2 text-sm"><Checkbox checked={s.payments?.manual_enabled} onCheckedChange={(v) => setS({ ...s, payments: { ...s.payments, manual_enabled: !!v } })} /> Manual payment methods</label>
        <div className="mt-3"><Label>Manual methods (comma-separated)</Label><Input value={(s.payments?.manual_methods || []).join(", ")} onChange={(e) => setS({ ...s, payments: { ...s.payments, manual_methods: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) } })} className="mt-1" /></div>
      </section>

      <Button onClick={save} disabled={saving} className="rounded-sm bg-sky-700 hover:bg-sky-800" data-testid="admin-save-settings">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Settings"}
      </Button>
    </div>
  );
}
